import React, { useState, useEffect } from 'react';
import './DateTimeSelectorStyles.css';

const DateTimeSelector = ({
    selectedDate,
    startTime,
    endTime,
    onDateChange,
    onStartTimeChange,
    onEndTimeChange,
    bookedDates = []
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDay, setSelectedDay] = useState(selectedDate || null);

    useEffect(() => {
        if (selectedDate) {
            setSelectedDay(selectedDate);
            setCurrentMonth(selectedDate);
        }
    }, [selectedDate]);

    const daysOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
    const hourOptions = ['12:00', '1:00', '2:00', '3:00', '4:00', '5:00', '6:00', '7:00', '8:00', '9:00', '10:00', '11:00'];

    const prevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const prevYear = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
    };

    const nextYear = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));
    };

    const isDateBooked = (date) => {
        return bookedDates.some(bookedDate => {
            const bookingDate = new Date(bookedDate);
            return (
                date.getDate() === bookingDate.getDate() &&
                date.getMonth() === bookingDate.getMonth() &&
                date.getFullYear() === bookingDate.getFullYear()
            );
        });
    };

    const isDateInPast = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const handleDateClick = (date) => {
        if (isDateInPast(date)) return; // Check past dates first
        if (isDateBooked(date)) return; // Then check if booked

        const newDate = new Date(date);
        setSelectedDay(newDate);
        onDateChange(newDate);
    };

    const renderCalendarDays = () => {
        const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

        // Get the first Monday before or on the month start
        const startDate = new Date(monthStart);
        const dayOfWeek = startDate.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
        startDate.setDate(startDate.getDate() - diff);

        const days = [];
        const rows = [];
        let day = new Date(startDate);
        let formattedDate = '';

        // Create 6 rows (enough for any month)
        for (let i = 0; i < 6; i++) {
            for (let j = 0; j < 7; j++) {
                formattedDate = day.getDate().toString();
                const cloneDay = new Date(day);

                // Check if date is in current month
                const isCurrentMonth = day.getMonth() === monthStart.getMonth();
                const isNextMonth = day > monthEnd && day.getMonth() === (monthStart.getMonth() + 1) % 12;
                const isPrevMonth = !isCurrentMonth && !isNextMonth;

                const isSelected = selectedDay &&
                    day.getDate() === selectedDay.getDate() &&
                    day.getMonth() === selectedDay.getMonth() &&
                    day.getFullYear() === selectedDay.getFullYear();

                const isBooked = isDateBooked(day);
                const isPast = isDateInPast(day);

                let className = '';
                if (isSelected) {
                    className = 'selected';
                } else if (isPast) {
                    className = 'disabled text-danger past-date'; // Past dates come first (including past booked dates)
                } else if (isBooked) {
                    className = 'disabled text-danger'; // Only for future booked dates
                } else if (isPrevMonth) {
                    className = 'adjacent-month'; // Only previous month gets adjacent-month class
                }

                days.push(
                    <div
                        key={day.toString()}
                        className={`calendar-day ${className}`}
                        onClick={() => {
                            // Allow clicks on current month and next month dates (if visible in current view)
                            // Check if past first, then check if booked
                            if ((isCurrentMonth || isNextMonth) && !isPast && !isBooked) {
                                handleDateClick(cloneDay);
                            }
                        }}
                    >
                        {formattedDate}
                    </div>
                );
                day.setDate(day.getDate() + 1);
            }
            rows.push(
                <div className="calendar-row" key={day.toString()}>
                    {days.slice(i * 7, (i + 1) * 7)}
                </div>
            );
        }
        return rows;
    };

    const handleTimeClick = (time, period, isStart) => {
        const formattedTime = `${time} ${period}`;
        if (isStart) {
            onStartTimeChange(formattedTime);

            // Reset end time if it's before the new start time
            if (endTime) {
                const [startHour, startMinute] = time.split(':');
                const [endHour, endMinute] = endTime.split(' ')[0].split(':');

                const startValue = parseInt(startHour) + (period === 'PM' && startHour !== '12' ? 12 : 0);
                const endValue = parseInt(endHour) + (endTime.includes('PM') && endHour !== '12' ? 12 : 0);

                if (startValue >= endValue) {
                    onEndTimeChange('');
                }
            }
        } else {
            onEndTimeChange(formattedTime);
        }
    };

    return (
        <div className="card shadow date-time-selector">
            <div className="card-body">
                <h5 className="card-title mb-4">Select Date and Time</h5>

                <div className="custom-calendar">
                    <div className="calendar-header">
                        <div className="nav-group">
                            <button className="nav-btn year-nav" onClick={prevYear}>&laquo;</button>
                            <button className="nav-btn month-nav" onClick={prevMonth}>&lt;</button>
                        </div>
                        <div className="calendar-month-title">
                            {currentMonth.toLocaleString('default', { month: 'long' })} {currentMonth.getFullYear()}
                        </div>
                        <div className="nav-group">
                            <button className="nav-btn month-nav" onClick={nextMonth}>&gt;</button>
                            <button className="nav-btn year-nav" onClick={nextYear}>&raquo;</button>
                        </div>
                    </div>

                    <div className="calendar-days-header">
                        {daysOfWeek.map(day => (
                            <div key={day} className="calendar-day-name">{day}</div>
                        ))}
                    </div>

                    <div className="calendar-body">
                        {renderCalendarDays()}
                    </div>
                </div>

                <div className="time-selection mt-4">
                    <div className="time-section">
                        <h6>Start Time</h6>
                        <div className="time-period-section">
                            <div className="period-label">AM</div>
                            <div className="time-buttons">
                                {hourOptions.map(hour => (
                                    <button
                                        key={`start-am-${hour}`}
                                        className={`btn btn-sm btn-outline-secondary time-btn ${startTime === `${hour} AM` ? 'active' : ''}`}
                                        onClick={() => handleTimeClick(hour, 'AM', true)}
                                    >
                                        {hour}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="time-period-section">
                            <div className="period-label">PM</div>
                            <div className="time-buttons">
                                {hourOptions.map(hour => (
                                    <button
                                        key={`start-pm-${hour}`}
                                        className={`btn btn-sm btn-outline-secondary time-btn ${startTime === `${hour} PM` ? 'active' : ''}`}
                                        onClick={() => handleTimeClick(hour, 'PM', true)}
                                    >
                                        {hour}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="time-section mt-4">
                        <h6>End Time</h6>
                        <div className="time-period-section">
                            <div className="period-label">AM</div>
                            <div className="time-buttons">
                                {hourOptions.map(hour => (
                                    <button
                                        key={`end-am-${hour}`}
                                        className={`btn btn-sm btn-outline-secondary time-btn ${endTime === `${hour} AM` ? 'active' : ''}`}
                                        onClick={() => handleTimeClick(hour, 'AM', false)}
                                    >
                                        {hour}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="time-period-section">
                            <div className="period-label">PM</div>
                            <div className="time-buttons">
                                {hourOptions.map(hour => (
                                    <button
                                        key={`end-pm-${hour}`}
                                        className={`btn btn-sm btn-outline-secondary time-btn ${endTime === `${hour} PM` ? 'active' : ''}`}
                                        onClick={() => handleTimeClick(hour, 'PM', false)}
                                        disabled={!startTime}
                                    >
                                        {hour}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DateTimeSelector;